/**
 * Hook for generating customer responses
 */
import { useState, useCallback } from 'react';
import {
  generateResponse,
  type RecordContext,
  type ResponseTone,
  type ResponseLength,
  type GeneratedResponse,
} from '@/services/api';

export function useResponseGenerator() {
  const [generatedResponse, setGeneratedResponse] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<GeneratedResponse['_meta'] | null>(null);

  const generate = useCallback(async (
    record: RecordContext,
    tone: ResponseTone,
    length: ResponseLength,
    customInstructions?: string
  ) => {
    setLoading(true);
    setError(null);
    setGeneratedResponse(null);
    setMeta(null);

    try {
      const result = await generateResponse(record, tone, length, customInstructions);
      setGeneratedResponse(result.response);
      setMeta(result._meta || null);
      return result.response;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate response';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setGeneratedResponse(null);
    setError(null);
    setMeta(null);
  }, []);

  return {
    generatedResponse,
    loading,
    error,
    meta,
    generate,
    clear,
  };
}
